/**
 * ตรรกะของหน้าจอ Tool/Die Master ที่แยกออกจาก React — ฟังก์ชันล้วน ทดสอบได้โดยไม่
 * ต้อง render (ฝาแฝดของ `lib/ncr/ncrView.ts`)
 *
 * ไฟล์นี้ถือสามข้อของ WO นี้:
 *
 * 1. **THE TWO-COUNTER HEADLINE** — `toolLifeHeadline` คืนสองค่าที่ **ไม่เคยรวมกัน**
 *    เป็นตัวเดียว พร้อมป้ายกำกับว่าใครใช้ตัวไหน (`references/tooling-mold-management.md`:
 *    "ห้ามรวมเป็นตัวเดียว")
 * 2. **NO BROWSER ARITHMETIC** — ไม่มีฟังก์ชันใดในไฟล์นี้บวก ลบ หรือ derive ตัวเลข
 *    ใหม่จากสองตัวนับ สิ่งเดียวที่ `ratedLifeStatus` ทำคือ**เทียบ**ค่าที่เซิร์ฟเวอร์ส่งมา
 *    กับ `rated_life_shots` เพื่อตัดสินป้ายที่แสดง ไม่ใช่คำนวณตัวนับตัวที่สาม
 * 3. **RATED LIFE เป็นสเปก ไม่ใช่ด่าน** — `ratedLifeStatus` ไม่เคยคืนค่าที่บอกว่า
 *    "ห้ามใช้" มีแค่ "อยู่ในเรต" กับ "ถึง/เกินเรตแล้ว" ทั้งสองแบบยังใช้งานเครื่องมือ
 *    ได้ปกติ — หน้าจอต้องไม่บล็อกอะไรจากค่านี้
 */

import { TOOL_TYPES, type Tool, type ToolTypeValue } from './types';

/** ฝาแฝดของ `StatusTone` ใน `desktop/StatusBadge.tsx` — restated แทนการ import
 * ข้าม layer เหตุผลเดียวกับ `lib/ncr/ncrView.ts` */
export type ToolStatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'planned';

/* --------------------------------------------------------------- ชนิด --- */

export function toolTypeLabel(type: string): string {
  switch (type) {
    case 'DIE':
      return 'แม่พิมพ์ตัด/ขึ้นรูป (Die)';
    case 'MOLD':
      return 'แม่พิมพ์ฉีด/หล่อ (Mold)';
    case 'JIG':
      return 'อุปกรณ์นำร่อง (Jig)';
    case 'FIXTURE':
      return 'อุปกรณ์จับยึด (Fixture)';
    case 'GAUGE':
      return 'เกจวัด (Gauge)';
    default:
      return type;
  }
}

export function isToolTypeValue(value: string): value is ToolTypeValue {
  return (TOOL_TYPES as readonly string[]).includes(value);
}

/* ---------------------------------------------------------- ตัวนับชีวิต -- */

/**
 * **THE TWO-COUNTER HEADLINE.** สองตัวเลขที่ตอบคนละคำถาม แยกกันเสมอ — ถอดฟังก์ชันนี้
 * ออกจากหน้าจอ (หรือเอาไปเรนเดอร์ทับกันเป็นตัวเดียว) ต้องทำให้เทสต์แดง
 * (`ToolRegistryScreen.test.tsx` — mutation-checkable)
 */
export interface ToolLifeHeadline {
  /** ทั้งชีวิตของเครื่องมือ — ใช้ตัดสินใจว่าเมื่อไรจะซื้อตัวใหม่ */
  readonly cumulativeLabel: string;
  readonly cumulativeValue: string;
  /** นับตั้งแต่ PM ล่าสุด — ใช้ตัดสินใจว่าเมื่อไรจะนัดซ่อมบำรุงครั้งถัดไป */
  readonly sinceMaintenanceLabel: string;
  readonly sinceMaintenanceValue: string;
}

export function toolLifeHeadline(tool: Tool): ToolLifeHeadline {
  return {
    cumulativeLabel: 'สะสมทั้งชีวิต (ตัดสินใจซื้อใหม่)',
    cumulativeValue: tool.cumulative_life_count,
    sinceMaintenanceLabel: 'นับตั้งแต่ซ่อมบำรุงล่าสุด (ตัดสินใจนัดซ่อมครั้งถัดไป)',
    sinceMaintenanceValue: tool.life_since_last_maintenance,
  };
}

/**
 * ความสัมพันธ์ระหว่างจำนวนที่ใช้จริง (สะสมทั้งชีวิต) กับเรตติ้ง — **ไม่เคยเป็นด่าน**
 * คืน `'unrated'` เมื่อยังไม่มีใครกรอกเรตติ้งไว้ (ไม่ใช่ "อยู่ในเกณฑ์" เพราะไม่มีเกณฑ์
 * ให้เทียบ) — เทียบด้วย `Number()` เพราะเป็นการ**แสดงความสัมพันธ์**ให้เห็น ไม่ใช่การ
 * คำนวณตัวนับตัวใหม่ (ดูหมายเหตุข้อ 2 ของไฟล์)
 */
export type RatedLifeStatus = 'unrated' | 'within-rating' | 'at-or-over-rating';

export function ratedLifeStatus(tool: Tool): RatedLifeStatus {
  if (tool.rated_life_shots === null) {
    return 'unrated';
  }
  const actual = Number(tool.cumulative_life_count);
  if (!Number.isFinite(actual)) {
    return 'unrated';
  }
  return actual >= tool.rated_life_shots ? 'at-or-over-rating' : 'within-rating';
}

export function ratedLifeStatusLabel(status: RatedLifeStatus): string {
  switch (status) {
    case 'unrated':
      return 'ยังไม่ระบุเรตติ้ง';
    case 'within-rating':
      return 'อยู่ในเรตติ้ง';
    case 'at-or-over-rating':
      return 'ถึง/เกินเรตติ้งแล้ว — ควรพิจารณาเปลี่ยน';
  }
}

export function ratedLifeStatusTone(status: RatedLifeStatus): ToolStatusTone {
  switch (status) {
    case 'unrated':
      return 'neutral';
    case 'within-rating':
      return 'success';
    case 'at-or-over-rating':
      return 'warning';
  }
}

/* ------------------------------------------------------- ฟอร์มลงทะเบียน -- */

export interface CreateToolFields {
  readonly toolCode: string;
  readonly toolType: string;
  readonly spec: string;
  readonly ratedLifeShots: string;
}

export const EMPTY_CREATE_TOOL_FIELDS: CreateToolFields = {
  toolCode: '',
  toolType: '',
  spec: '',
  ratedLifeShots: '',
};

/** แปลงข้อความในช่องเรตติ้งเป็นจำนวนเต็มบวก — คืน `undefined` เมื่อช่องว่าง (ไม่ส่ง
 * ฟิลด์) และคืน `null` เมื่อกรอกแต่ค่าไม่ถูกกฎ (ไม่ใช่จำนวนเต็มบวก) เพื่อให้ปุ่มส่ง
 * ปิดไว้จนกว่าจะแก้ */
function parseRatedLifeShotsInput(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return undefined;
  }
  if (!/^\d+$/.test(trimmed)) {
    return null;
  }
  const value = Number(trimmed);
  return value > 0 ? value : null;
}

/**
 * ประกอบ body ของ `POST /v1/tools` — ทุกฟิลด์ไม่บังคับ จึงไม่มีวันคืน `null` เพราะ
 * ฟอร์มว่างทั้งหมดก็ยังลงทะเบียนได้ (เครื่องมือที่ยังไม่มีใครจำแนกอะไรเลย) **ยกเว้น**
 * เมื่อเรตติ้งกรอกมาแต่ไม่ถูกกฎ ซึ่งคืน `null` เพื่อปิดปุ่มส่ง
 */
export function toCreateToolBody(
  fields: CreateToolFields,
): { readonly tool_code?: string; readonly tool_type?: ToolTypeValue; readonly spec?: string; readonly rated_life_shots?: number } | null {
  const toolCode = fields.toolCode.trim();
  const toolType = fields.toolType.trim();
  const spec = fields.spec.trim();
  const ratedLifeShots = parseRatedLifeShotsInput(fields.ratedLifeShots);

  if (ratedLifeShots === null) {
    return null;
  }
  if (toolType !== '' && !isToolTypeValue(toolType)) {
    return null;
  }

  return {
    ...(toolCode === '' ? {} : { tool_code: toolCode }),
    ...(toolType === '' ? {} : { tool_type: toolType }),
    ...(spec === '' ? {} : { spec }),
    ...(ratedLifeShots === undefined ? {} : { rated_life_shots: ratedLifeShots }),
  };
}

/* ---------------------------------------------------------- ฟอร์มแก้ไข --- */

export interface EditToolFields {
  readonly toolCode: string;
  readonly toolType: string;
  readonly spec: string;
  readonly ratedLifeShots: string;
}

/** เตรียมฟอร์มแก้ไขจากค่าปัจจุบันของเครื่องมือ — `null` กลายเป็นสตริงว่างเสมอ */
export function editToolFieldsFromTool(tool: Tool): EditToolFields {
  return {
    toolCode: tool.tool_code ?? '',
    toolType: tool.tool_type ?? '',
    spec: tool.spec ?? '',
    ratedLifeShots: tool.rated_life_shots === null ? '' : String(tool.rated_life_shots),
  };
}

/**
 * ประกอบ body ของ `PATCH /v1/tools/{tool_id}` — ส่งครบทั้งสี่คีย์เสมอ (ฟอร์มเต็มที่
 * แสดงค่าปัจจุบัน) ช่องที่เว้นว่างไว้กลายเป็น `null` ซึ่งฝั่งเซิร์ฟเวอร์อ่านว่า "ลบ
 * ค่านี้" โดยตั้งใจ — คืน `null` (ปิดปุ่มส่ง) เมื่อเรตติ้งกรอกมาแต่ไม่ถูกกฎ
 */
export function toPatchToolBody(
  fields: EditToolFields,
): { readonly tool_code: string | null; readonly tool_type: ToolTypeValue | null; readonly spec: string | null; readonly rated_life_shots: number | null } | null {
  const toolCode = fields.toolCode.trim();
  const toolType = fields.toolType.trim();
  const spec = fields.spec.trim();
  const ratedLifeShots = parseRatedLifeShotsInput(fields.ratedLifeShots);

  if (ratedLifeShots === null) {
    return null;
  }
  if (toolType !== '' && !isToolTypeValue(toolType)) {
    return null;
  }

  return {
    tool_code: toolCode === '' ? null : toolCode,
    tool_type: toolType === '' ? null : toolType,
    spec: spec === '' ? null : spec,
    rated_life_shots: ratedLifeShots === undefined ? null : ratedLifeShots,
  };
}

/* --------------------------------------------------------- many-to-many -- */

/** ประกอบ body ของ `POST /v1/tools/{tool_id}/products` — คืน `null` จนกว่าจะกรอก
 * `product_id` (ช่องข้อความดิบ — หน้าจอนี้ยังไม่มีตัวเลือกสินค้าให้ค้นหา) */
export function toLinkToolProductBody(
  productIdInput: string,
): { readonly product_id: string } | null {
  const trimmed = productIdInput.trim();
  return trimmed === '' ? null : { product_id: trimmed };
}

/** ประกอบ body ของ `POST /v1/tools/{tool_id}/work-centers` — เหตุผลเดียวกับข้างบน */
export function toLinkToolWorkCenterBody(
  workCenterIdInput: string,
): { readonly work_center_id: string } | null {
  const trimmed = workCenterIdInput.trim();
  return trimmed === '' ? null : { work_center_id: trimmed };
}

/* ------------------------------------------------------------- ซ่อมบำรุง -- */

/**
 * ประกอบ body ของ `POST /v1/tools/{tool_id}/maintenance-events` — `performedAtInput`
 * มาจาก `<input type="datetime-local">` (ไม่มี timezone) แปลงผ่าน `Date` ของ
 * เบราว์เซอร์ให้เป็น ISO ก่อนส่ง คืน `null` จนกว่าจะกรอกวันที่/เวลาที่อ่านได้
 */
export function toRecordToolMaintenanceBody(
  performedAtInput: string,
  noteInput: string,
): { readonly performed_at: string; readonly note?: string } | null {
  const trimmed = performedAtInput.trim();
  if (trimmed === '') {
    return null;
  }
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  const note = noteInput.trim();
  return {
    performed_at: parsed.toISOString(),
    ...(note === '' ? {} : { note }),
  };
}

/* -------------------------------------------------------------- ตัวกรอง -- */

export function toToolTypeFilter(raw: string): ToolTypeValue | null {
  return isToolTypeValue(raw) ? raw : null;
}
